/**
 * Sugerencia de tiempos de SLA a partir del histórico de tickets
 * (Configuración → Tiempos de SLA → Autocalcular). Lógica pura.
 *
 * Para cada tipo × prioridad toma el percentil (80 por defecto) de los tiempos
 * reales de primera respuesta y de resolución, y lo redondea a un valor
 * "operable". Con pocos datos no inventa: usa el conjunto de esa prioridad y,
 * si tampoco alcanza, conserva el valor actual. Cada sugerencia indica su
 * origen y cuántos tickets la respaldan.
 */

const MIN = 60 * 1000

/**
 * Percentil por interpolación lineal.
 * @param {number[]} values
 * @param {number} p - Entre 0 y 1.
 * @returns {number|null}
 */
export const percentile = (values, p) => {
  if (!values.length) return null
  const sorted = [...values].sort((a, b) => a - b)
  const idx = (sorted.length - 1) * p
  const lo = Math.floor(idx)
  const hi = Math.ceil(idx)
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo)
}

/**
 * Redondea hacia arriba a un valor fácil de comprometer: tramos de 15 min
 * hasta 2 h, de 1 h hasta 2 días y de 12 h en adelante. Mínimo 15 min.
 * @param {number} mins
 * @returns {number}
 */
export const niceMinutes = (mins) => {
  const m = Math.max(15, mins)
  if (m <= 120) return Math.ceil(m / 15) * 15
  if (m <= 48 * 60) return Math.ceil(m / 60) * 60
  return Math.ceil(m / 720) * 720
}

/** Minutos entre dos fechas, o null si faltan o el orden es inválido. */
const spanMins = (from, to) => {
  if (!from || !to) return null
  const diff = (new Date(to).getTime() - new Date(from).getTime()) / MIN
  return diff >= 0 ? diff : null
}

/**
 * Sugiere políticas de SLA a partir del histórico.
 *
 * @param {Array<Object>} tickets - Con type, priorityId, createdAt,
 *   firstResponseAt, resolvedAt.
 * @param {Object} options
 * @param {string[]} options.types - Tipos a calcular.
 * @param {number[]} options.priorityIds - Prioridades a calcular.
 * @param {Object} options.current - Políticas actuales (formato SLA_POLICIES).
 * @param {number} [options.p=0.8] - Percentil.
 * @param {number} [options.minSamples=3] - Mínimo de tickets para confiar.
 * @returns {Record<string, Record<number, {responseMins: number|null,
 *   resolutionMins: number|null, samples: number,
 *   source: 'cell'|'priority'|'current'}>>}
 */
export const suggestSlaPolicies = (
  tickets = [],
  { types, priorityIds, current = {}, p = 0.8, minSamples = 3 }
) => {
  const collect = (list) => ({
    response: list
      .map((t) => spanMins(t.createdAt, t.firstResponseAt))
      .filter((v) => v !== null),
    resolution: list
      .map((t) => spanMins(t.createdAt, t.resolvedAt))
      .filter((v) => v !== null),
  })

  const fromSamples = (data) => {
    const response = niceMinutes(percentile(data.response, p))
    // La resolución nunca puede ser antes que la primera respuesta.
    const resolution = Math.max(
      response,
      niceMinutes(percentile(data.resolution, p))
    )
    return { responseMins: response, resolutionMins: resolution }
  }

  const enough = (data) =>
    data.response.length >= minSamples && data.resolution.length >= minSamples

  const result = {}
  for (const type of types) {
    result[type] = {}
    for (const priorityId of priorityIds) {
      const cell = collect(
        tickets.filter(
          (t) => t.type === type && Number(t.priorityId) === Number(priorityId)
        )
      )
      const prio = collect(
        tickets.filter((t) => Number(t.priorityId) === Number(priorityId))
      )
      const existing = current[type]?.[priorityId] ?? null

      if (enough(cell)) {
        result[type][priorityId] = {
          ...fromSamples(cell),
          samples: cell.resolution.length,
          source: 'cell',
        }
      } else if (enough(prio)) {
        result[type][priorityId] = {
          ...fromSamples(prio),
          samples: prio.resolution.length,
          source: 'priority',
        }
      } else {
        result[type][priorityId] = {
          responseMins: existing?.responseMins ?? null,
          resolutionMins: existing?.resolutionMins ?? null,
          samples: cell.resolution.length,
          source: 'current',
        }
      }
    }
  }
  return result
}

/* ── Matriz editable (Configuración → Tiempos de SLA) ───────────────────── */

/** Minutos → texto de horas para un input (vacío si no hay valor). */
const toHours = (mins) =>
  mins == null ? '' : String(Math.round((mins / 60) * 100) / 100)

/** Texto de horas → minutos enteros (null si está vacío o no es válido). */
const toMins = (hours) => {
  const text = String(hours ?? '').trim()
  const n = Number(text.replace(',', '.'))
  return text === '' || !Number.isFinite(n) || n <= 0
    ? null
    : Math.round(n * 60)
}

/**
 * Borrador editable a partir de políticas (formato SLA_POLICIES).
 * @param {Object} policies
 * @param {string[]} types
 * @param {number[]} priorityIds
 * @returns {Record<string, {enabled: boolean,
 *   cells: Record<number, {response: string, resolution: string}>}>}
 */
export const draftFromPolicies = (policies, types, priorityIds) =>
  Object.fromEntries(
    types.map((type) => {
      const byType = policies?.[type] ?? null
      return [
        type,
        {
          enabled: byType !== null,
          cells: Object.fromEntries(
            priorityIds.map((pid) => [
              pid,
              {
                response: toHours(byType?.[pid]?.responseMins),
                resolution: toHours(byType?.[pid]?.resolutionMins),
              },
            ])
          ),
        },
      ]
    })
  )

/**
 * Aplica sugerencias (de `suggestSlaPolicies`) a los tipos con SLA activo.
 * @param {Object} draft
 * @param {Object} suggestions
 * @returns {Object} Borrador nuevo.
 */
export const applySuggestions = (draft, suggestions) =>
  Object.fromEntries(
    Object.entries(draft).map(([type, row]) => {
      if (!row.enabled || !suggestions[type]) return [type, row]
      const cells = { ...row.cells }
      for (const [pid, s] of Object.entries(suggestions[type])) {
        if (s.responseMins == null) continue
        cells[pid] = {
          response: toHours(s.responseMins),
          resolution: toHours(s.resolutionMins),
        }
      }
      return [type, { ...row, cells }]
    })
  )

/**
 * Valida el borrador. Devuelve `"tipo:prioridad"` → mensaje.
 * @param {Object} draft
 * @returns {Record<string, string>}
 */
export const validateSlaDraft = (draft) => {
  const errors = {}
  for (const [type, row] of Object.entries(draft)) {
    if (!row.enabled) continue
    for (const [pid, cell] of Object.entries(row.cells)) {
      const response = toMins(cell.response)
      const resolution = toMins(cell.resolution)
      const key = `${type}:${pid}`
      if ((response == null) !== (resolution == null))
        errors[key] = 'Complete ambos tiempos o deje los dos vacíos.'
      else if (response != null && resolution < response)
        errors[key] = 'La resolución no puede ser menor que la respuesta.'
    }
  }
  return errors
}

/**
 * Filas para `Tickets.slaPolicy` a partir del borrador. Un tipo sin SLA
 * guarda todas sus celdas en null; una celda vacía queda sin SLA.
 * @param {Object} draft
 * @returns {Array<{type: string, priorityId: number,
 *   responseMins: number|null, resolutionMins: number|null}>}
 */
export const rowsFromDraft = (draft) =>
  Object.entries(draft).flatMap(([type, row]) =>
    Object.entries(row.cells).map(([pid, cell]) => ({
      type,
      priorityId: Number(pid),
      responseMins: row.enabled ? toMins(cell.response) : null,
      resolutionMins: row.enabled ? toMins(cell.resolution) : null,
    }))
  )
